import { objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import modalManager from './modalManager.js';
import modalProps from './modalProps.js';

var keys = {
  ArrowDown: {
    code: 'ArrowDown',
    keyCode: 40
  },
  ArrowLeft: {
    code: 'ArrowLeft',
    keyCode: 37
  },
  ArrowRight: {
    code: 'ArrowRight',
    keyCode: 39
  },
  ArrowUp: {
    code: 'ArrowUp',
    keyCode: 38
  },
  End: {
    code: 'End',
    keyCode: 35
  },
  Enter: {
    code: 'Enter',
    keyCode: 13
  },
  Escape: {
    code: 'Escape',
    keyCode: 27
  },
  Home: {
    code: 'Home',
    keyCode: 36
  },
  Space: {
    code: 'Space',
    keyCode: 32
  },
  Tab: {
    code: 'Tab',
    keyCode: 9
  }
};
var checkKeyEvent = function checkKeyEvent(event) {
  return function (key) {
    return !!(event.code && keys[key].code === event.code || event.keyCode && keys[key].keyCode === event.keyCode);
  };
};
var whenKeyIs = function whenKeyIs(code) {
  return function (action) {
    return function (event) {
      if (checkKeyEvent(event)(code)) {
        action(event);
      }
    };
  };
};
var whenKeyIsEscape = whenKeyIs('Escape');
var hasTabIndex = function hasTabIndex(target) {
  return target.getAttribute('tabindex') === '0';
};
var isFocusableAnchor = function isFocusableAnchor(target) {
  return target.nodeName === 'A' && !!target.getAttribute('href') && target.getAttribute('rel') !== 'ignore';
};
var isFocusableInput = function isFocusableInput(target) {
  return target.nodeName === 'INPUT' && ['file', 'hidden'].indexOf(target.getAttribute('type')) === -1;
};
var isOtherFocusableNode = function isOtherFocusableNode(target) {
  return ['BUTTON', 'SELECT', 'TEXTAREA'].indexOf(target.nodeName) > -1;
};
var isFocusableNode = function isFocusableNode(target) {
  return isFocusableAnchor(target) || isFocusableInput(target) || isOtherFocusableNode(target);
};
var isVisible = function isVisible(target) {
  return window.getComputedStyle(target).visibility !== 'hidden';
};
var isFocusable = function isFocusable(target) {
  return (hasTabIndex(target) || isFocusableNode(target)) && target.getAttribute('disabled') === null && isVisible(target);
};
var manager = modalManager();
var propTypes = _objectSpread2(_objectSpread2({}, modalProps), {}, {
  /**
   * use class list for own styles
   */
  classList: PropTypes.string,
  isToaster: PropTypes.bool,
  /**
   * use styles for inline styles
   */
  style: PropTypes.objectOf(PropTypes.string)
});
function ModalContent(_ref) {
  var children = _ref.children,
    closeButtonTheme = _ref.closeButtonTheme,
    closeButtonLabel = _ref.closeButtonLabel,
    _ref$classList = _ref.classList,
    classList = _ref$classList === void 0 ? '' : _ref$classList,
    footer = _ref.footer,
    heading = _ref.heading,
    id = _ref.id,
    isFullBleed = _ref.isFullBleed,
    isFullscreen = _ref.isFullscreen,
    isOpen = _ref.isOpen,
    isSticky = _ref.isSticky,
    isToaster = _ref.isToaster,
    onClose = _ref.onClose,
    onMouseLeave = _ref.onMouseLeave,
    onOpen = _ref.onOpen,
    style = _ref.style,
    variant = _ref.variant;
  var modalRef = useRef(null);
  var modalHeadingRef = useRef(null);
  var firstTabRef = useRef(null);
  var lastTabRef = useRef(null);
  var modalClasses = "modal-block ".concat(classList);
  var handleOpen = useCallback(function (event) {
    var _modalHeadingRef$curr;
    manager.push(modalRef === null || modalRef === void 0 ? void 0 : modalRef.current, document.body);
    if (isToaster && modalRef.current) modalRef.current.style.opacity = 1;
    modalHeadingRef === null || modalHeadingRef === void 0 || (_modalHeadingRef$curr = modalHeadingRef.current) === null || _modalHeadingRef$curr === void 0 || _modalHeadingRef$curr.focus();
    onOpen(event);
  }, [onOpen, modalRef, modalHeadingRef, isToaster]);
  var focusFirstTab = function focusFirstTab(tabList) {
    var _tabList$;
    return (_tabList$ = tabList[0]) === null || _tabList$ === void 0 ? void 0 : _tabList$.focus();
  };
  var focusLastTab = function focusLastTab(tabList) {
    var _tabList;
    return (_tabList = tabList[tabList.length - 1]) === null || _tabList === void 0 ? void 0 : _tabList.focus();
  };
  var handleTabFocus = useCallback(function (tab, focusFn) {
    var excludeList = ['modal-backdrop', 'tab-panel'];
    var includes = function includes(exclusions) {
      return function (aClass) {
        return exclusions.some(function (value) {
          return aClass === value;
        });
      };
    };
    var isExcluded = function isExcluded(target) {
      return _toConsumableArray(target.classList).some(includes(excludeList));
    };
    var isNotTagRef = function isNotTagRef(target) {
      return target !== (firstTabRef === null || firstTabRef === void 0 ? void 0 : firstTabRef.current) && target !== (lastTabRef === null || lastTabRef === void 0 ? void 0 : lastTabRef.current);
    };
    var naturalTabs = _toConsumableArray(modalRef === null || modalRef === void 0 ? void 0 : modalRef.current.querySelectorAll('*')).filter(function (nTab) {
      return isFocusable(nTab) && !isExcluded(nTab) && isNotTagRef(nTab);
    });
    focusFn(naturalTabs);
  }, [firstTabRef, lastTabRef]);
  var escapeKeyClose = function escapeKeyClose(event) {
    event.stopPropagation();
    onClose(event);
  };
  var handleKeyDown = whenKeyIsEscape(escapeKeyClose);

  /**
   * Without an empty array as a dependency, useEffect runs when the modal opens
   * and closes which will cause critical a11y errors in Toaster if onOpen is passed
   * and the callback is defined in the scope of the template.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleOpen, []);
  useEffect(function () {
    return function () {
      return manager.pop();
    };
  }, []);
  return /*#__PURE__*/React__default.createElement("div", {
    className: "modal modal-z-index",
    "data-full-bleed": isFullBleed,
    "data-full-screen": isFullscreen,
    "data-toaster-open": isOpen,
    "data-open": isOpen,
    "data-variant": variant,
    "data-sticky-header": isSticky,
    id: id,
    onKeyDown: handleKeyDown,
    ref: modalRef,
    role: "presentation",
    tabIndex: -1
  }, /*#__PURE__*/React__default.createElement("div", {
    onFocus: function onFocus() {
      return handleTabFocus(firstTabRef.current, focusLastTab);
    },
    ref: firstTabRef
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    ,
    tabIndex: 0
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "modal-overflow-wrapper"
  }, /*#__PURE__*/React__default.createElement("button", {
    "aria-hidden": "true",
    className: "modal-backdrop",
    onClick: onClose,
    tabIndex: -1,
    type: "button"
  }), /*#__PURE__*/React__default.createElement("div", {
    "aria-labelledby": "".concat(id, "-label"),
    className: modalClasses,
    role: "dialog",
    style: style,
    onMouseLeave: onMouseLeave
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "modal-heading"
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "modal-heading-title",
    id: "".concat(id, "-label"),
    ref: modalHeadingRef,
    tabIndex: -1
  }, heading), /*#__PURE__*/React__default.createElement("button", {
    className: "modal-close-button",
    "data-close-button-theme": closeButtonTheme,
    onClick: onClose,
    type: "button"
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "screen-reader-text"
  }, closeButtonLabel), /*#__PURE__*/React__default.createElement("span", {
    "aria-hidden": "true",
    className: "modal-close-icon"
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "modal-content"
  }, children), footer)), /*#__PURE__*/React__default.createElement("div", {
    onFocus: function onFocus() {
      return handleTabFocus(lastTabRef.current, focusFirstTab);
    },
    ref: lastTabRef
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    ,
    tabIndex: 0
  }));
}
ModalContent.propTypes = propTypes;

export { ModalContent as default, keys };
//# sourceMappingURL=ModalContent.js.map
